/**
 * 커스텀 Modal – alert / confirm 대체.
 * 전역에서 사용: Modal.alert(message), Modal.confirm(message).
 * 스타일: resources/scss/admin/components/_app-modal.scss (인라인 스타일 미사용).
 */

const CONTAINER_ID = "app-modal-root";
const BACKDROP_CLASS = "app-modal-backdrop";
const BOX_CLASS = "app-modal-box";
const HEADER_CLASS = "app-modal-header";
const TITLE_CLASS = "app-modal-title";
const CLOSE_CLASS = "app-modal-close";
const BODY_CLASS = "app-modal-body";
const FOOTER_CLASS = "app-modal-footer";
const BTN_CLASS = "app-modal-btn";
const BTN_PRIMARY_CLASS = "app-modal-btn--primary";

let activeModalClose: (() => void) | null = null;

function getModalLabel(key: "ok" | "cancel" | "close" | "alert_title" | "confirm_title"): string {
  const attr = document.documentElement.getAttribute(`data-modal-${key === "alert_title" ? "alert-title" : key === "confirm_title" ? "confirm-title" : key}`);
  if (attr) return attr;
  const fallback: Record<string, string> = {
    ok: "확인",
    cancel: "취소",
    close: "닫기",
    alert_title: "알림",
    confirm_title: "확인",
  };
  return fallback[key] ?? "";
}

function getContainer(): HTMLDivElement {
  let root = document.getElementById(CONTAINER_ID) as HTMLDivElement | null;
  if (!root) {
    root = document.createElement("div");
    root.id = CONTAINER_ID;
    document.body.appendChild(root);
  }
  return root;
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function closeActiveModal(): void {
  if (activeModalClose) {
    activeModalClose();
  }
}

/** 알림 한 개만 띄우고 확인 버튼으로 닫기. opts.onOk 지정 시 확인 버튼 클릭에만 호출됨. */
export function alert(message: string, title?: string, opts?: { onOk?: () => void }): Promise<void> {
  closeActiveModal();

  const defaultTitle = getModalLabel("alert_title") || "알림";
  const okLabel = getModalLabel("ok") || "확인";
  const closeLabel = getModalLabel("close") || "닫기";
  const titleText = title ?? defaultTitle;
  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  box.className = BOX_CLASS;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-app-modal-close aria-label="${escapeHtml(closeLabel)}">&times;</button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-app-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);
  root.appendChild(backdrop);

  return new Promise((resolve) => {
    let closed = false;
    const okButton = box.querySelector<HTMLButtonElement>("[data-app-modal-ok]");
    const removeListeners = (): void => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
    const finalize = (): void => {
      if (closed) return;
      closed = true;
      removeListeners();
      backdrop.remove();
      if (activeModalClose === closeOnly) {
        activeModalClose = null;
      }
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      resolve();
    };
    const closeOnly = (): void => {
      finalize();
    };
    const closeWithOk = (): void => {
      finalize();
      opts?.onOk?.();
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Enter" && e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.key === "Enter") {
        closeWithOk();
      } else {
        closeOnly();
      }
    };

    activeModalClose = closeOnly;
    document.addEventListener("keydown", onKeyDown, true);
    okButton?.addEventListener("click", closeWithOk);
    box.querySelector("[data-app-modal-close]")?.addEventListener("click", closeOnly);
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) closeOnly();
    });
    box.addEventListener("click", (e) => e.stopPropagation());
    window.setTimeout(() => okButton?.focus(), 0);
  });
}

/** 확인/취소 선택, 확인 true / 취소 false */
export function confirm(message: string, title?: string): Promise<boolean> {
  closeActiveModal();

  const defaultTitle = getModalLabel("confirm_title") || "확인";
  const okLabel = getModalLabel("ok") || "확인";
  const cancelLabel = getModalLabel("cancel") || "취소";
  const closeLabel = getModalLabel("close") || "닫기";
  const titleText = title ?? defaultTitle;
  const previouslyFocused = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  box.className = BOX_CLASS;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-app-modal-close aria-label="${escapeHtml(closeLabel)}">&times;</button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS}" data-app-modal-cancel>${escapeHtml(cancelLabel)}</button>
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-app-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);
  root.appendChild(backdrop);

  return new Promise((resolve) => {
    let closed = false;
    const okButton = box.querySelector<HTMLButtonElement>("[data-app-modal-ok]");
    const removeListeners = (): void => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
    const close = (value: boolean) => (): void => {
      if (closed) return;
      closed = true;
      removeListeners();
      backdrop.remove();
      if (activeModalClose === closeFalse) {
        activeModalClose = null;
      }
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      resolve(value);
    };
    const closeFalse = close(false);
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Enter" && e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.key === "Enter") {
        close(true)();
      } else {
        closeFalse();
      }
    };

    activeModalClose = closeFalse;
    document.addEventListener("keydown", onKeyDown, true);
    okButton?.addEventListener("click", close(true));
    box.querySelector("[data-app-modal-cancel]")?.addEventListener("click", close(false));
    box.querySelector("[data-app-modal-close]")?.addEventListener("click", closeFalse);
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) closeFalse();
    });
    box.addEventListener("click", (e) => e.stopPropagation());
    window.setTimeout(() => okButton?.focus(), 0);
  });
}

export const Modal = { alert, confirm };
